import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { ContractLineBillingCycleOutput, ContractStatusEnum, VariableContractLineOutput }
  from '@/graphql/contracts/_generated_/contracts';
import { clearVariableLinesSelection, useSelectedVariableLines }
  from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import { clearLinesSelection, useSelectedLines }
  from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import OptionButton from '@/components/common/OptionButton/OptionButton';
import EditIcon from '@/components/common/OptionButton/icons/Edit/EditIcon';
import { useBulkUnassignPurchaseOrder } from '@/pages/ContractDetails/ContractLines/hooks/useBulkUnassignPurchaseOrder';
import UnassignPurchaseOrderModal from './UnassignPurchaseOrderModal';
import { rightsidePanel_isOpen } from '../../RightPanel.state';

export default function UnassignPurchaseOrder() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const variableSelected = Boolean(selectedVariableLineRows?.length);

  const [isOpenUnassignPurchaseOrderModal, setIsOpenUnassignPurchaseOrderModal] = useState(false);
  const { bulkUnassignPurchaseOrder } = useBulkUnassignPurchaseOrder(true, contractId, variableSelected);
  const [totalLinesCount, setTotalLinesCount] = useState<number>(0);
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);

  const handleSave = () => {
    bulkUnassignPurchaseOrder([
      ...selectedLineRows.map(row => row.id),
      ...selectedVariableLineRows.map(row => row.id)
    ], () => {
      clearLinesSelection();
      clearVariableLinesSelection();
      setIsOpenRightPanel(false);
    });
    setIsOpenUnassignPurchaseOrderModal(false);
  };

  const handleOnClick = () => {
    setIsOpenUnassignPurchaseOrderModal(true);
    setTotalLinesCount(selectedLineRows.length + selectedVariableLineRows.length);
  };

  const getContractLineStatus =
    (contractLine: ContractLineBillingCycleOutput | VariableContractLineOutput) => {
      return ((contractLine as ContractLineBillingCycleOutput)?.status ||
        (contractLine as VariableContractLineOutput)?.status) as
        | ContractStatusEnum
        | undefined;
    };

  const editableStatuses = [
    ContractStatusEnum.Active,
    ContractStatusEnum.ActiveDNR,
  ];

  if (
    !selectedLineRows.filter((slr) =>
      editableStatuses.includes(
        getContractLineStatus(slr.payload.contractLine) ??
        ContractStatusEnum.Cancelled
      )
    ).length &&
    !selectedVariableLineRows.filter((svlr) =>
      editableStatuses.includes(
        getContractLineStatus(svlr.payload.contractLine) ??
        ContractStatusEnum.Cancelled
      )
    ).length
  ) {
    return null;
  }

  return (
    <>
      <OptionButton onClick={handleOnClick}
        Icon={< EditIcon />}>
        Unassign Purchase Order
      </OptionButton>

      <UnassignPurchaseOrderModal
        isOpen={isOpenUnassignPurchaseOrderModal}
        totalLinesCount={totalLinesCount}
        onConfirm={handleSave}
        onClose={() => setIsOpenUnassignPurchaseOrderModal(false)}
      />
    </>
  );
}
